'use client'

import {
  Globe,
  Search,
  BookOpen,
  MessageSquare,
  Star,
  TrendingUp,
  TrendingDown,
  Shield,
  AlertTriangle,
  FileText,
  BarChart3,
  Target,
  Zap,
} from 'lucide-react'

/**
 * Branding + GEO Diagrams
 * Visual components for the AI Search Playbook companion post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * Brand Authority Framework - where AI models get brand citations
 */
export function BrandAuthorityFramework() {
  const signals = [
    {
      source: 'Wikipedia / Wikidata',
      share: '26.3%',
      icon: BookOpen,
      color: '#008dcc',
      tip: 'Create or update your entity page with accurate, verifiable facts',
    },
    {
      source: 'Reddit Discussions',
      share: '1.8%',
      icon: MessageSquare,
      color: '#F90B8A',
      tip: 'Engage authentically in subreddits where your audience asks questions',
    },
    {
      source: 'Industry Publications',
      share: '1.1%+',
      icon: FileText,
      color: '#8b5cf6',
      tip: 'Contribute expert commentary and get mentioned in roundups',
    },
    {
      source: 'Review Platforms (G2, Capterra)',
      share: '1.1%+',
      icon: Star,
      color: '#FDC304',
      tip: 'Encourage genuine reviews with specific outcomes',
    },
    {
      source: 'Your Own Website',
      share: 'Indirect',
      icon: Globe,
      color: '#008dcc',
      tip: 'Structured data + consistent entity attributes across all pages',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='mb-4 flex items-center gap-2'>
        <Target className='h-5 w-5 text-[#008dcc]' />
        <span className='text-xl md:text-2xl font-bold text-foreground'>
          Where AI Models Get Brand Citations
        </span>
      </div>
      <p className='mb-4 text-sm text-muted-foreground'>
        Source: The Digital Bloom AI Visibility Report
      </p>

      <div className='mb-5 px-4 py-3 bg-[#FDC304]/10 rounded-lg border border-[#FDC304]/30'>
        <p className='text-sm font-medium text-foreground'>
          Key insight: Brands are{' '}
          <span className='font-bold text-[#FDC304]'>6.5x</span> more likely to
          be cited via third-party sources than through their own domains
        </p>
      </div>

      <div className='space-y-3'>
        {signals.map(signal => {
          const Icon = signal.icon
          return (
            <div
              key={signal.source}
              className='p-3 rounded-lg bg-background border border-border/60 flex items-start gap-3'
            >
              <Icon
                className='mt-0.5 h-4 w-4 shrink-0'
                style={{ color: signal.color }}
              />
              <div className='flex-1'>
                <div className='flex items-center justify-between'>
                  <span className='text-sm font-semibold text-foreground'>
                    {signal.source}
                  </span>
                  <span
                    className='text-sm font-bold'
                    style={{ color: signal.color }}
                  >
                    {signal.share}
                  </span>
                </div>
                <p className='mt-1 text-xs text-muted-foreground'>
                  {signal.tip}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      <div className='mt-5 px-4 py-2 bg-muted rounded-lg'>
        <p className='text-center text-sm text-muted-foreground'>
          <span className='font-semibold text-foreground'>
            Brand search volume
          </span>{' '}
          is the #1 predictor of AI citations (0.334 correlation), stronger than
          backlinks.
        </p>
      </div>
    </div>
  )
}

/**
 * AI Brand Story Framework - anatomy of a citeable story
 */
export function AIBrandStoryFramework() {
  const layers = [
    {
      label: 'Named Framework',
      human: 'Memorable, brandable process',
      ai: 'Unique entity AI can reference by name',
      example: '"The Sprint-to-Scale Method"',
      icon: Zap,
      color: '#F90B8A',
    },
    {
      label: 'Specific Metrics',
      human: 'Builds credibility and trust',
      ai: 'Extractable data points for citation',
      example: '"34% increase in 90 days"',
      icon: BarChart3,
      color: '#008dcc',
    },
    {
      label: 'Before/After Contrast',
      human: 'Shows transformation clearly',
      ai: 'Structured comparison AI models prefer',
      example: '"4.2s → 1.1s load time"',
      icon: TrendingUp,
      color: '#FDC304',
    },
    {
      label: 'Third-Party Validation',
      human: 'Social proof and credibility',
      ai: 'Cross-source confirmation (10x weight)',
      example: '"Featured on G2, Clutch, TechCrunch"',
      icon: Shield,
      color: '#8b5cf6',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='mb-6 flex items-center gap-2'>
        <FileText className='h-5 w-5 text-[#F90B8A]' />
        <span className='text-xl md:text-2xl font-bold text-foreground'>
          Anatomy of an AI-Citeable Brand Story
        </span>
      </div>

      <div className='space-y-4'>
        {layers.map(layer => {
          const Icon = layer.icon
          return (
            <div
              key={layer.label}
              className='p-4 rounded-lg border-2'
              style={{ borderColor: `${layer.color}40` }}
            >
              <div className='mb-3 flex items-center gap-2'>
                <Icon className='h-4 w-4' style={{ color: layer.color }} />
                <span
                  className='text-sm font-bold'
                  style={{ color: layer.color }}
                >
                  {layer.label}
                </span>
              </div>
              <div className='grid gap-2 sm:grid-cols-2'>
                <div className='p-3 rounded-lg bg-muted'>
                  <span className='text-xs font-medium text-muted-foreground'>
                    For Humans
                  </span>
                  <p className='text-sm text-foreground/90'>{layer.human}</p>
                </div>
                <div className='p-3 rounded-lg bg-muted'>
                  <span className='text-xs font-medium text-muted-foreground'>
                    For AI Models
                  </span>
                  <p className='text-sm text-foreground/90'>{layer.ai}</p>
                </div>
              </div>
              <p className='mt-2 text-center text-xs text-muted-foreground'>
                Example: {layer.example}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Brand AI Citation Gap - cited vs not-cited impact
 */
export function BrandAICitationGap() {
  const metrics = [
    {
      label: 'Organic CTR (not cited)',
      value: '-61%',
      trend: 'down',
      color: '#F90B8A',
    },
    {
      label: 'Paid CTR (not cited)',
      value: '-68%',
      trend: 'down',
      color: '#F90B8A',
    },
    {
      label: 'Organic clicks (cited)',
      value: '+35%',
      trend: 'up',
      color: '#10b981',
    },
    {
      label: 'Paid clicks (cited)',
      value: '+91%',
      trend: 'up',
      color: '#10b981',
    },
  ]

  const drift = [
    { platform: 'Google AI Overviews', drift: '59.3%', color: '#008dcc' },
    { platform: 'ChatGPT', drift: '54.1%', color: '#10b981' },
    { platform: 'Microsoft Copilot', drift: '53.4%', color: '#8b5cf6' },
    { platform: 'Perplexity', drift: '~40%', color: '#FDC304' },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <div className='flex items-center justify-center gap-2 mb-2'>
          <Search className='h-5 w-5 text-[#F90B8A]' />
          <span className='text-xl md:text-2xl font-bold text-foreground'>
            The AI Citation Gap
          </span>
        </div>
        <p className='text-sm text-muted-foreground'>
          When AI Overviews appear: cited vs. not cited
        </p>
      </div>

      <div className='mb-5 grid grid-cols-2 gap-3'>
        {metrics.map(metric => {
          const TrendIcon = metric.trend === 'up' ? TrendingUp : TrendingDown
          return (
            <div
              key={metric.label}
              className='p-3 rounded-lg bg-background border border-border/60 text-center'
            >
              <div className='mb-1 flex items-center justify-center gap-1'>
                <TrendIcon
                  className='h-4 w-4'
                  style={{ color: metric.color }}
                />
                <span
                  className='text-xl font-bold'
                  style={{ color: metric.color }}
                >
                  {metric.value}
                </span>
              </div>
              <p className='text-xs text-muted-foreground'>{metric.label}</p>
            </div>
          )
        })}
      </div>

      <div className='p-4 rounded-lg bg-background border border-border/60'>
        <div className='mb-3 flex items-center gap-2'>
          <AlertTriangle className='h-4 w-4 text-[#FDC304]' />
          <span className='text-sm font-semibold text-foreground'>
            Monthly Citation Drift (domains that change)
          </span>
        </div>
        <div className='space-y-2'>
          {drift.map(item => (
            <div
              key={item.platform}
              className='flex items-center justify-between'
            >
              <span className='text-sm text-muted-foreground'>
                {item.platform}
              </span>
              <div className='flex items-center gap-2'>
                <div className='h-2 w-24 sm:w-32 overflow-hidden rounded-full bg-muted'>
                  <div
                    className='h-full rounded-full'
                    style={{
                      width: item.drift,
                      backgroundColor: item.color,
                    }}
                  />
                </div>
                <span
                  className='text-sm font-bold'
                  style={{ color: item.color }}
                >
                  {item.drift}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className='mt-4 px-4 py-2 bg-[#008dcc]/10 rounded-lg border border-[#008dcc]/30'>
        <p className='text-center text-xs text-muted-foreground'>
          Source: Seer Interactive (3,119 search terms, 42 organizations) and
          Profound (AI Platform Citation Patterns)
        </p>
      </div>
    </div>
  )
}
